import React from 'react';
import { ListBlock } from './ListBlock';
import { FilterItem } from '../../model/FilterItem';
import { FilterType } from '../../model/FilterType';
import { Collapsed } from '../../model/Collapsed';
import { t } from '../../i18n';

// The API sends zone, gate and crossing line in the same parameter (zone_hits),
// so only one of the three can be active at a time
const zoneHitsTypes = [ FilterType.zone, FilterType.gate, FilterType.crossingLine ];

const List = ({ textFilter, type, locations, installations, campaigns, playlists, zones, gates, crossingLines, selected, showZonesAndGates, onAdd, onRemove, onChangeType }: Props) => {
  const onCheckItem = (item: Collapsed, type: FilterType, value: boolean) => {
    if(value && type !== FilterType.location) {
      const types = zoneHitsTypes.includes(type) ? zoneHitsTypes : [ type ];
      const selected = types.flatMap(filterType => getSelected(filterType));
      for(let i of selected) {
        onRemove(i);
      }
    }
    const e = { value: item.id, type: type, text: item.name };
    value ? onAdd(e) : onRemove(e);
  }

  const getFiltered = (list: Collapsed[]): Collapsed[] => {
    return list.filter(item => item.name.toLowerCase().includes(textFilter.toLowerCase()));
  }

  const getFilteredLocations = (): Collapsed[] => {
    return textFilter ? getFiltered(locations) : locations;
  }

  const getFilteredInstallations = (): Collapsed[] => {
    let filteredInstallations = installations;
    const selectedLocations = getSelected(FilterType.location).map(l => l.value);
    if(selectedLocations.length) {
      const filteredInstallationsMap = new Map<string, any>();
      for(let location of locations) {
        if(selectedLocations.includes(location.id)) {
          for(let installation of (location as any).installations) {
            if(!filteredInstallationsMap.has(installation.id)) {
              filteredInstallationsMap.set(installation.id, installation);
            }
          }
        }
      }
      filteredInstallations = Array.from(filteredInstallationsMap.values());
    }
    return textFilter ? getFiltered(filteredInstallations) : filteredInstallations;
  }

  const getFilteredCampaigns = (): Collapsed[] => {
    let filteredCampaigns = campaigns;
    const selectedInstallations = getSelected(FilterType.installation).map(i => i.value);
    if(selectedInstallations.length) {
      const filteredCampaignsMap = new Map<string, any>();
      for(let location of locations) {
        for(let installation of (location as any).installations) {
          if(selectedInstallations.includes(installation.id)) {
            for(let campaign of installation.campaigns) {
              if(!filteredCampaignsMap.has(campaign.id)) {
                filteredCampaignsMap.set(campaign.id, campaign);
              }
            }
          }
        }
      }
      filteredCampaigns = Array.from(filteredCampaignsMap.values());
    }
    else {
      const selectedLocations = getSelected(FilterType.location).map(l => l.value);
      if(selectedLocations.length) {
        const filteredCampaignsMap = new Map<string, any>();
        for(let location of locations) {
          if(selectedLocations.includes(location.id)) {
            for(let installation of (location as any).installations) {
              for(let campaign of installation.campaigns) {
                if(!filteredCampaignsMap.has(campaign.id)) {
                  filteredCampaignsMap.set(campaign.id, campaign);
                }
              }
            }
          }
        }
        filteredCampaigns = Array.from(filteredCampaignsMap.values());
      }
    }
    return textFilter ? getFiltered(filteredCampaigns) : filteredCampaigns;
  }

  // Playlists de las campañas que encajan con lo seleccionado en cada nivel
  // (ubicación → instalación → campaña); un nivel sin selección no restringe.
  const getFilteredPlaylists = (): Collapsed[] => {
    let filteredPlaylists = playlists;
    const selectedLocations = getSelected(FilterType.location).map(l => l.value);
    const selectedInstallations = getSelected(FilterType.installation).map(i => i.value);
    const selectedCampaigns = getSelected(FilterType.campaign).map(c => c.value);
    if(selectedLocations.length || selectedInstallations.length || selectedCampaigns.length) {
      const filteredPlaylistsMap = new Map<string, any>();
      for(let location of locations) {
        if(selectedLocations.length && !selectedLocations.includes(location.id)) {
          continue;
        }
        for(let installation of (location as any).installations) {
          if(selectedInstallations.length && !selectedInstallations.includes(installation.id)) {
            continue;
          }
          for(let campaign of installation.campaigns) {
            if(selectedCampaigns.length && !selectedCampaigns.includes(campaign.id)) {
              continue;
            }
            for(let playlist of campaign.playlists || []) {
              if(!filteredPlaylistsMap.has(playlist.id)) {
                filteredPlaylistsMap.set(playlist.id, playlist);
              }
            }
          }
        }
      }
      filteredPlaylists = Array.from(filteredPlaylistsMap.values());
    }
    return textFilter ? getFiltered(filteredPlaylists) : filteredPlaylists;
  }

  const getFilteredZones = (): Collapsed[] => {
    return textFilter ? getFiltered(zones) : zones;
  }

  const getFilteredGates = (): Collapsed[] => {
    return textFilter ? getFiltered(gates) : gates;
  }

  const getFilteredCrossingLines = (): Collapsed[] => {
    return textFilter ? getFiltered(crossingLines) : crossingLines;
  }

  const getSelected = (type: FilterType): FilterItem[] => {
    return selected.filter(i => i.type === type);
  }

  return (
    <div>
      {type == FilterType.all || type === FilterType.location ? <ListBlock items={getFilteredLocations()} selected={getSelected(FilterType.location).map(i => i.value)} expanded={type === FilterType.location} onCheck={(item: Collapsed, value: boolean) => onCheckItem(item, FilterType.location, value)} title={t('filter.listTitles.location')} viewMoreLabel={t('filter.viewMore.location')} icon="location" color="info" onExpand={() => onChangeType(FilterType.location)} /> : ''}
      {type === FilterType.all || type === FilterType.installation ? <ListBlock items={getFilteredInstallations()} selected={getSelected(FilterType.installation).map(i => i.value)} expanded={type === FilterType.installation} onCheck={(item: Collapsed, value: boolean) => onCheckItem(item, FilterType.installation, value)} title={t('filter.listTitles.installation')} viewMoreLabel={t('filter.viewMore.installation')} icon="rotate-dot" color="warning" onExpand={() => onChangeType(FilterType.installation)} /> : ''}
      {type === FilterType.all || type === FilterType.campaign ? <ListBlock items={getFilteredCampaigns()} selected={getSelected(FilterType.campaign).map(i => i.value)} expanded={type === FilterType.campaign} onCheck={(item: Collapsed, value: boolean) => onCheckItem(item, FilterType.campaign, value)} title={t('filter.listTitles.campaign')} viewMoreLabel={t('filter.viewMore.campaign')} icon="device-analytics" color="info" onExpand={() => onChangeType(FilterType.campaign)} /> : ''}
      {type === FilterType.all || type === FilterType.playlist ? <ListBlock items={getFilteredPlaylists()} selected={getSelected(FilterType.playlist).map(i => i.value)} expanded={type === FilterType.playlist} onCheck={(item: Collapsed, value: boolean) => onCheckItem(item, FilterType.playlist, value)} title={t('filter.listTitles.playlist')} viewMoreLabel={t('filter.viewMore.playlist')} icon="playlist" color="info" onExpand={() => onChangeType(FilterType.playlist)} /> : ''}
      {showZonesAndGates && (type === FilterType.all || type === FilterType.zone) ? <ListBlock items={getFilteredZones()} selected={getSelected(FilterType.zone).map(i => i.value)} expanded={type === FilterType.zone} onCheck={(item: Collapsed, value: boolean) => onCheckItem(item, FilterType.zone, value)} title={t('filter.listTitles.zone')} viewMoreLabel={t('filter.viewMore.zone')} icon="map-pin" color="info" onExpand={() => onChangeType(FilterType.zone)} /> : ''}
      {showZonesAndGates && (type === FilterType.all || type === FilterType.gate) ? <ListBlock items={getFilteredGates()} selected={getSelected(FilterType.gate).map(i => i.value)} expanded={type === FilterType.gate} onCheck={(item: Collapsed, value: boolean) => onCheckItem(item, FilterType.gate, value)} title={t('filter.listTitles.gate')} viewMoreLabel={t('filter.viewMore.gate')} icon="door" color="info" onExpand={() => onChangeType(FilterType.gate)} /> : ''}
      {showZonesAndGates && (type === FilterType.all || type === FilterType.crossingLine) ? <ListBlock items={getFilteredCrossingLines()} selected={getSelected(FilterType.crossingLine).map(i => i.value)} expanded={type === FilterType.crossingLine} onCheck={(item: Collapsed, value: boolean) => onCheckItem(item, FilterType.crossingLine, value)} title={t('filter.listTitles.crossingLine')} viewMoreLabel={t('filter.viewMore.crossingLine')} icon="line" color="info" onExpand={() => onChangeType(FilterType.crossingLine)} /> : ''}
    </div>
  );
}

interface Props {
  textFilter: string,
  type: FilterType,
  locations: Collapsed[],
  installations: Collapsed[],
  campaigns: Collapsed[],
  playlists: Collapsed[],
  zones: Collapsed[],
  gates: Collapsed[],
  crossingLines: Collapsed[],
  selected: FilterItem[],
  showZonesAndGates: boolean,
  onAdd: (item: FilterItem) => void,
  onRemove: (item: FilterItem) => void,
  onChangeType: (type: FilterType) => void
}

export { List };
